import { create } from "zustand";
import type { User, Session } from "@supabase/supabase-js";
import { getClient } from "./client.js";

interface AuthState {
  user: User | null;
  session: Session | null;
  /** True while the initial getSession() call is in-flight on app boot. */
  loading: boolean;
  /**
   * Call once at app boot. Reads the current session (handles magic-link
   * redirect tokens via detectSessionInUrl) and subscribes to
   * onAuthStateChange so the store stays reactive.
   */
  init: () => void;
  /**
   * Send a magic-link to the given email. The user clicks the link in their
   * inbox; the SDK exchanges the token automatically on the next page load
   * because detectSessionInUrl is enabled.
   * Returns { error: null } on success, { error: message } on failure.
   */
  signInWithEmail: (email: string) => Promise<{ error: string | null }>;
  signInWithGoogle: () => Promise<{ error: string | null }>;
  signOut: () => Promise<void>;
  /**
   * Sign in with email + password. Used by the demo persona switcher to
   * programmatically log in as a seeded test user without a magic link.
   */
  signInWithPassword: (email: string, password: string) => Promise<{ error: string | null }>;
  /**
   * Permanently delete the current user's account and all their remote data.
   * Calls the `delete_own_account` Supabase RPC (security-definer function
   * that removes the row from auth.users, cascading to cards/prefs/reviews).
   * Signs out locally on success.
   */
  deleteAccount: () => Promise<{ error: string | null }>;
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  session: null,
  loading: true,

  init() {
    const supabase = getClient();
    if (!supabase) {
      set({ loading: false });
      return;
    }
    void supabase.auth.getSession().then(({ data }) => {
      set({
        session: data.session,
        user: data.session?.user ?? null,
        loading: false,
      });
    });
    supabase.auth.onAuthStateChange((_event, session) => {
      set({ session, user: session?.user ?? null });
    });
  },

  async signInWithEmail(email) {
    const supabase = getClient();
    if (!supabase) return { error: "Sync is not configured in this build." };
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: { emailRedirectTo: `${window.location.origin}/auth/callback` },
    });
    return { error: error?.message ?? null };
  },

  async signInWithGoogle() {
    const supabase = getClient();
    if (!supabase) return { error: "Sync is not configured in this build." };
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: { redirectTo: `${window.location.origin}/auth/callback` },
    });
    return { error: error?.message ?? null };
  },

  async signInWithPassword(email, password) {
    const supabase = getClient();
    if (!supabase) return { error: "Sync is not configured in this build." };
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error: error?.message ?? null };
  },

  async signOut() {
    const supabase = getClient();
    if (supabase) await supabase.auth.signOut();
    set({ user: null, session: null });
  },

  async deleteAccount() {
    const supabase = getClient();
    if (!supabase) return { error: "Sync is not configured in this build." };
    const { error } = await supabase.rpc("delete_own_account");
    if (!error) {
      await supabase.auth.signOut();
      set({ user: null, session: null });
    }
    return { error: error?.message ?? null };
  },
}));
